/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { find, get } from "lodash"
import { storageHelper } from "utils/storage"
import { UserTypes } from "constants/tracking"
import { Link, ParentEntityType } from "types/models/user_profile.type"
import { GF, GP, MERCHANT } from "constants/user"

const USER_TYPES = [
  UserTypes.merchant,
  UserTypes.consumer,
  UserTypes.store,
  UserTypes.individualMerchant,
  UserTypes.individualStore,
  UserTypes.mlmMerchant,
  UserTypes.mlmStore,
  UserTypes.gtwyMerchant,
  UserTypes.gtwyStore,
  UserTypes.ptnrMerchant,
  UserTypes.ptnrStore,
  UserTypes.offlineAcquirer,
]

const getLinks = () =>
  storageHelper?.getUserProfile()?.user_profile?.links ?? []

export const getUserStoreID = () => {
  const links = getLinks()

  const linkEntityID = find(
    links,
    l =>
      l.link_entity_business_line === "GF" ||
      l.link_entity_business_line === "OLD_GPGF",
  )?.link_entity_id

  if (linkEntityID) return linkEntityID

  // fallback value if links are empty
  const grabFoodEntityID = storageHelper?.getUserProfile()?.user_profile
    ?.grab_food_entity_id

  return grabFoodEntityID
}

export const getUserGrabPayGrabID = () => {
  const links = getLinks()

  const link = find(links, l => l.link_entity_business_line === "GP")

  return link?.link_entity_id
}

export const getUserType = (num: number) => {
  if (num >= 0 && num <= USER_TYPES.length) {
    return USER_TYPES[num - 1]
  }
  return ""
}

export const getMerchantGrabID = () => {
  const userProfile = storageHelper.getUserProfile()?.user_profile
  const userProfileLinks = userProfile?.links || []
  return get(
    userProfileLinks.find(
      data => data.link_entity_business_line === "GP_MERCHANT",
    ),
    ["link_entity_id"],
  )
}

export const getUserProfileLinkByBusinessLine = (
  businessLine: string,
): Link => {
  return (
    getLinks().find(link => link.link_entity_business_line === businessLine) ||
    ({} as Link)
  )
}

export const getUserProfileServiceID = (): number => {
  return storageHelper?.getUserProfile()?.user_profile?.service_id || 0
}

export const getUserProfileParentEntityID = (): string => {
  return storageHelper?.getUserProfile()?.user_profile?.parent_entity_id || ""
}

export const isObserver = (): boolean => {
  return storageHelper?.getUserProfile()?.user_profile?.is_observer || false
}

export const isCreatedAtMerchantLevel = (links: Link[]): boolean => {
  if (!links) {
    return false
  }

  const merchantTypes = [
    ParentEntityType.INDIVIDUAL_MERCHANT,
    ParentEntityType.MLM_MERCHANT,
    ParentEntityType.GTWY_MERCHANT,
    ParentEntityType.PTNR_MERCHANT,
    ParentEntityType.OFFLINE_ACQUIRER,
  ]

  return links.some(link => {
    // Case 1: Numeric merchant type with GrabPay
    const numericType = parseInt(link.link_entity_type, 10)

    if (
      !Number.isNaN(numericType) &&
      merchantTypes.includes(numericType) &&
      link.link_entity_business_line === GP
    ) {
      return true
    }

    // Case 2: String "MERCHANT" with GrabFood
    if (
      link.link_entity_business_line === GF &&
      link.link_entity_type === MERCHANT
    ) {
      return true
    }

    return false
  })
}
